import { useCallback, useEffect, useState, type JSX } from 'react';
import { api, type Certificate } from '../../api.ts';
import { formatDate } from '../Certificates.tsx';

/**
 * What the organisation has issued.
 *
 * Withdrawing is the only action here, and it asks for a reason: the number is already printed on
 * something a person is carrying around, so "withdrawn" with no stated cause is a dispute the
 * teacher will have to reconstruct from memory months later.
 */
export function AdminCertificatesPage(): JSX.Element {
  const [certificates, setCertificates] = useState<Certificate[] | null>(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback((): void => {
    api.authoring
      .certificates()
      .then(setCertificates)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить сертификаты'),
      );
  }, []);

  useEffect(load, [load]);

  /**
   * Send the holder round again.
   *
   * Offered from the certificate rather than from the student list because this is the moment it
   * comes up: the twelve months on this sheet are running out and the training has to be redone.
   * The certificate stays as it is — the person did pass, on the date it says.
   */
  async function retake(certificate: Certificate): Promise<void> {
    const confirmed = window.confirm(
      `Назначить перепрохождение курса «${certificate.course_title}» для ученика ${certificate.student_name}?

` +
        'Прогресс по курсу обнулится, и его нужно будет пройти заново. ' +
        'Выданный сертификат останется — он подтверждает прошлое обучение.',
    );
    if (!confirmed) return;

    setBusy(certificate.id);
    setError('');

    try {
      const result = await api.authoring.retake(certificate.enrollment_id);
      setNote(`Назначено перепрохождение, цикл ${result.cycle_number}.`);
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось назначить');
    } finally {
      setBusy('');
    }
  }

  async function revoke(certificate: Certificate): Promise<void> {
    const reason = window.prompt(
      `Почему отзывается сертификат ${certificate.number}?\nПричину увидит только организация.`,
    );
    if (!reason || reason.trim().length < 3) return;

    setBusy(certificate.id);
    setError('');

    try {
      await api.authoring.revokeCertificate(certificate.id, reason.trim());
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось отозвать');
    } finally {
      setBusy('');
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Выданные сертификаты</h1>
        <p className="muted">
          Выдаются автоматически, когда ученик закрывает курс. Номер проверяется публично, без входа
          в систему.
        </p>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {note ? <div className="notice">{note}</div> : null}
      {certificates === null && !error ? <p className="muted">Загружаем…</p> : null}
      {certificates?.length === 0 ? (
        <div className="notice">
          Пока ничего не выдано. Проверьте, включена ли выдача в настройках курса.
        </div>
      ) : null}

      {(certificates ?? []).map((certificate) => (
        <div key={certificate.id} className="editrow">
          <span className="editrow__main">
            <strong>{certificate.student_name}</strong>
            <span className="muted"> · {certificate.course_title}</span>
          </span>
          <span className="certcard__number certcard__number--inline">{certificate.number}</span>
          <span className="muted" style={{ fontSize: '0.82rem' }}>
            {formatDate(certificate.issued_at)}
          </span>

          {certificate.revoked_at ? (
            <span className="badge">отозван</span>
          ) : certificate.is_expired ? (
            <span className="badge badge--wait">срок истёк</span>
          ) : (
            <span className="badge badge--live">действителен</span>
          )}

          <a
            className="btn btn--ghost btn--small"
            href={api.authoring.certificatePdfUrl(certificate.id)}
            download
          >
            PDF
          </a>

          <button
            type="button"
            className="btn btn--ghost btn--small"
            disabled={busy === certificate.id}
            onClick={() => void retake(certificate)}
          >
            Перепрохождение
          </button>

          {certificate.revoked_at ? null : (
            <button
              type="button"
              className="btn btn--ghost btn--small btn--danger"
              disabled={busy === certificate.id}
              onClick={() => void revoke(certificate)}
            >
              Отозвать
            </button>
          )}
        </div>
      ))}
    </>
  );
}
